import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { StatCard, Panel, BiasBadge, LiveDot, DemoTag, AiText, Spinner, fmt } from "../components/ui-kit";
import { PriceSpark } from "../components/Charts";
import { Sparkles, Send, TrendingUp, TrendingDown, Users, Flame, MousePointerClick, BadgeCheck, Radio, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function fmtCountdown(target) {
  const diff = Math.max(0, Math.floor((new Date(target) - new Date()) / 1000));
  const h = String(Math.floor(diff / 3600)).padStart(2, "0");
  const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
  const s = String(diff % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function NewsLockBanner() {
  const [lock, setLock] = useState(null);
  const [, force] = useState(0);
  useEffect(() => {
    const load = () => client.get("/economic/news-lock").then(({ data }) => setLock(data)).catch(() => {});
    load();
    const l = setInterval(load, 60000);
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => { clearInterval(l); clearInterval(t); };
  }, []);
  if (!lock) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 fade-up" data-testid="news-countdown">
      {["XAUUSD", "BTCUSD"].map((inst) => {
        const d = lock[inst] || {};
        const ev = d.next_high_impact;
        const accent = inst === "XAUUSD" ? "gold" : "btc";
        return (
          <div key={inst} data-testid={`news-countdown-${inst}`}
            className={`surface p-3 flex items-center gap-3 border-l-2 ${d.locked ? "border-bear" : (accent === "gold" ? "border-gold" : "border-btc")}`}>
            <AlertTriangle size={16} className={d.locked ? "text-bear" : "text-slate-500"} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`font-mono text-xs ${accent === "gold" ? "text-gold" : "text-btc"}`}>{inst}</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-500">next high-impact</span>
                {d.locked && <span className="text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 border border-bear text-bear animate-pulse">News Lock Active</span>}
              </div>
              <div className="text-xs text-slate-300 truncate mt-0.5">{ev ? ev.title : "No high-impact events scheduled"}</div>
            </div>
            {ev && (
              <div className="text-right shrink-0">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">in</div>
                <div className={`font-mono text-base ${d.locked ? "text-bear" : "text-white"}`}>{fmtCountdown(ev.time)}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MarketTile({ inst, d, accent, spark }) {
  const up = d.change_pct >= 0;
  const sparkData = spark && spark.length > 1 ? spark : d.spark;
  return (
    <div className="surface p-4 flex flex-col gap-3" data-testid={`market-tile-${inst}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`font-head font-semibold ${accent === "gold" ? "text-gold" : "text-btc"}`}>{inst}</span>
          <span className="text-xs text-slate-500">{d.name}</span>
          <span className={`text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 border ${d.demo === false ? "border-[color:var(--bull)] text-bull" : "border-slate-600 text-slate-500"}`}>{d.demo === false ? "Live" : "Sim"}</span>
        </div>
        <LiveDot />
      </div>
      <div className="flex items-end justify-between">
        <div className="font-mono text-3xl font-semibold text-white tracking-tight tabular-nums transition-colors duration-300" data-testid={`market-price-${inst}`}>{fmt(d.price)}</div>
        <div className={`font-mono text-sm flex items-center gap-1 ${up ? "text-bull" : "text-bear"}`}>
          {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}{up ? "+" : ""}{fmt(d.change_pct)}%
        </div>
      </div>
      <div className="h-[52px] -mx-1">
        <PriceSpark data={sparkData} color={accent === "gold" ? "#D4AF37" : "#F7931A"} height={52} />
      </div>
      <div className="flex items-center justify-between text-xs">
        <BiasBadge bias={d.bias} />
        <span className="font-mono text-slate-500">Vol {fmt(d.volatility)}%</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [brief, setBrief] = useState("");
  const [briefLoading, setBriefLoading] = useState(true);
  const [cmd, setCmd] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const [series, setSeries] = useState({ XAUUSD: [], BTCUSD: [] });

  useEffect(() => {
    client.get("/dashboard").then(({ data }) => setData(data));
    client.get("/dashboard/what-matters").then(({ data }) => setBrief(data.brief)).finally(() => setBriefLoading(false));
    const seed = (price, vol, dec) => Array.from({ length: 24 }, (_, i) =>
      ({ p: +(price * (1 + (Math.sin(i / 3.5) * 0.4 + (Math.random() - 0.5)) * (vol / 100) * 0.12)).toFixed(dec) }));
    const pull = async () => {
      try {
        const { data } = await client.get("/market/overview");
        setOverview(data);
        setSeries((prev) => {
          const next = {};
          ["XAUUSD", "BTCUSD"].forEach((k) => {
            const dec = k === "BTCUSD" ? 1 : 2;
            const base = prev[k]?.length ? prev[k] : seed(data[k].price, data[k].volatility || 1, dec);
            next[k] = [...base, { p: data[k].price }].slice(-48);
          });
          return next;
        });
      } catch { /* keep last */ }
    };
    pull();
    const t = setInterval(pull, 3000);
    return () => clearInterval(t);
  }, []);

  const ask = async (e) => {
    e.preventDefault();
    if (!cmd.trim()) return;
    setAsking(true); setAnswer("");
    try {
      const { data } = await client.post("/ai/command", { prompt: cmd });
      setAnswer(data.answer);
    } catch { toast.error("AI command failed"); } finally { setAsking(false); }
  };

  if (!data || !overview) return <Layout title="Command Center"><Spinner /></Layout>;

  const examples = ["Why is Gold moving?", "Analyze BTC right now", "Show today's high-intent leads", "Generate today's Telegram Gold post"];

  return (
    <Layout title="AI Command Center">
      {/* AI command box */}
      <div className="surface p-4 mb-5 fade-up">
        <form onSubmit={ask} className="flex items-center gap-2">
          <Sparkles size={18} className="text-gold shrink-0" />
          <input data-testid="ai-command-input" value={cmd} onChange={(e) => setCmd(e.target.value)}
            placeholder="Ask anything — Why is Gold moving? Show hot leads. Generate a Gold post…"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-600 focus:outline-none" />
          <button data-testid="ai-command-send" disabled={asking} className="btn-gold px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60">
            <Send size={14} />{asking ? "…" : "Ask"}
          </button>
        </form>
        <div className="flex flex-wrap gap-2 mt-3">
          {examples.map((e) => (
            <button key={e} onClick={() => setCmd(e)} className="text-xs px-2 py-1 border border-[color:var(--border-subtle)] text-slate-400 hover:border-gold hover:text-gold transition-colors duration-200">{e}</button>
          ))}
        </div>
        {(asking || answer) && (
          <div className="mt-4 pt-4 border-t border-[color:var(--border-subtle)]" data-testid="ai-command-answer">
            {asking ? <Spinner label="Thinking" /> : <AiText text={answer} />}
          </div>
        )}
      </div>

      <NewsLockBanner />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        {/* Left: markets + what matters */}
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MarketTile inst="XAUUSD" d={overview.XAUUSD} accent="gold" spark={series.XAUUSD} />
            <MarketTile inst="BTCUSD" d={overview.BTCUSD} accent="btc" spark={series.BTCUSD} />
          </div>

          <Panel title="What Matters Right Now" right={<DemoTag />} testid="what-matters-panel">
            <div className="p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-gold" />
              {briefLoading ? <Spinner label="Generating brief" /> : <AiText text={brief} />}
            </div>
          </Panel>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testid="stat-active-signals" label="Active Signals" value={data.signals.active} accent="bull" icon={<Radio size={15} className="text-slate-600" />} />
            <StatCard testid="stat-developing" label="Developing" value={data.signals.developing} sub="setups forming" />
            <StatCard testid="stat-pending" label="Pending Approval" value={data.signals.pending} accent="gold" icon={<Clock size={15} className="text-slate-600" />} />
            <StatCard testid="stat-model-r" label="Model P&L Today" value={`${data.model_r_today >= 0 ? "+" : ""}${fmt(data.model_r_today)}R`} accent={data.model_r_today >= 0 ? "bull" : "bear"} />
          </div>
        </div>

        {/* Right: business */}
        <div className="space-y-4">
          <Panel title="Business Snapshot">
            <div className="grid grid-cols-2 gap-px" style={{ background: "var(--border-subtle)" }}>
              {[
                { l: "New Leads Today", v: data.leads.new_today, i: <Users size={14} /> },
                { l: "Hot Leads", v: data.leads.hot, i: <Flame size={14} className="text-bear" /> },
                { l: "Broker Clicks", v: data.brokers.clicks, i: <MousePointerClick size={14} /> },
                { l: "Verified Clients", v: data.brokers.verified, i: <BadgeCheck size={14} className="text-bull" /> },
                { l: "Free Telegram", v: data.telegram.free.toLocaleString() },
                { l: "Premium Telegram", v: data.telegram.premium.toLocaleString() },
                { l: "Broker Apps", v: data.brokers.applications },
                { l: "Expiring Premium", v: data.telegram.expiring },
              ].map((s, i) => (
                <div key={i} className="p-3" style={{ background: "var(--surface)" }}>
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">{s.i}{s.l}</div>
                  <div className="font-mono text-xl text-white mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Instrument Bias">
            {["XAUUSD", "BTCUSD"].map((inst) => (
              <div key={inst} className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-subtle)] last:border-0">
                <div>
                  <div className={`font-mono text-sm ${inst === "XAUUSD" ? "text-gold" : "text-btc"}`}>{inst}</div>
                  <div className="text-xs text-slate-500">{data.market[inst].indicators.momentum} · RSI {data.market[inst].indicators.rsi}</div>
                </div>
                <BiasBadge bias={data.market[inst].bias} />
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </Layout>
  );
}
